import { divideRounded } from '@fd/money';
import { addDays, daysBetween, type IsoDate } from './dates.ts';
import type { TransactionKind, TransactionStatus } from './types.ts';

export type ForecastEntry = { readonly date: IsoDate; readonly amountMinor: bigint };
export type ForecastPoint = {
  readonly date: IsoDate;
  readonly balanceMinor: bigint;
  readonly changeMinor: bigint;
};

/** A single-currency, end-of-day projection. Past entries are reported, never silently charged again. */
export function forecastBalances(
  openingMinor: bigint,
  from: IsoDate,
  until: IsoDate,
  entries: readonly ForecastEntry[],
) {
  const length = daysBetween(from, until);
  if (length < 0 || length > 366)
    throw new RangeError('Forecast must cover between 0 and 366 days');
  const changes = new Map<IsoDate, bigint>();
  let overdueCount = 0;
  for (const entry of entries) {
    if (entry.date < from) {
      overdueCount++;
      continue;
    }
    if (entry.date > until) continue;
    changes.set(entry.date, (changes.get(entry.date) ?? 0n) + entry.amountMinor);
  }
  let balance = openingMinor;
  let minimumMinor = openingMinor;
  let minimumDate = from;
  let firstNegativeDate: IsoDate | null = openingMinor < 0n ? from : null;
  const points: ForecastPoint[] = [];
  for (let i = 0; i <= length; i++) {
    const date = addDays(from, i);
    const changeMinor = changes.get(date) ?? 0n;
    balance += changeMinor;
    if (balance < minimumMinor) {
      minimumMinor = balance;
      minimumDate = date;
    }
    if (balance < 0n && firstNegativeDate === null) firstNegativeDate = date;
    points.push({ date, balanceMinor: balance, changeMinor });
  }
  return {
    points,
    openingMinor,
    closingMinor: balance,
    minimumMinor,
    minimumDate,
    firstNegativeDate,
    overdueCount,
  };
}

export type SpendingTransaction = {
  readonly id: string;
  readonly accountId: string;
  readonly bookingDate: IsoDate;
  /** Signed from the account's perspective: outflow negative. */
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly kind: TransactionKind;
  readonly status: TransactionStatus;
};

/**
 * Everyday spending per day, for the days between recurring payments: booked expenses of the
 * given accounts (refunds deducted) over the `days` days before today, without the payments
 * of recurring patterns — the forecast already has those on their own dates. A shorter history
 * counts from its first transaction. Never negative.
 */
export function averageDailySpending(
  transactions: readonly SpendingTransaction[],
  options: {
    readonly accountIds: ReadonlySet<string>;
    readonly currency: string;
    readonly today: IsoDate;
    readonly recurringIds: ReadonlySet<string>;
    readonly days?: number;
  },
): bigint {
  const from = addDays(options.today, -(options.days ?? 90));
  let first: IsoDate | null = null;
  let spentMinor = 0n;
  for (const t of transactions) {
    if (!options.accountIds.has(t.accountId) || t.currency !== options.currency) continue;
    if (t.bookingDate >= options.today) continue;
    if (first === null || t.bookingDate < first) first = t.bookingDate;
    if (t.bookingDate < from || t.status !== 'BOOKED' || t.kind !== 'EXPENSE') continue;
    if (options.recurringIds.has(t.id)) continue;
    spentMinor -= t.amountMinor;
  }
  if (first === null || spentMinor <= 0n) return 0n;
  const span = daysBetween(first > from ? first : from, options.today);
  return span > 0 ? divideRounded(spentMinor, BigInt(span), 'HALF_UP') : 0n;
}
